"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CommentsService = void 0;
const core_1 = require("@capacitor/core");
const firestore_1 = require("firebase/firestore");
const firebaseManager_1 = require("./firebaseManager");
const unifiedFirestoreService_1 = require("./unifiedFirestoreService");
const POLL_INTERVAL_MS = 15000;
class CommentsService {
    static addComment(parent, text, authorUid, authorName, authorAvatar) {
        return __awaiter(this, void 0, void 0, function* () {
            const now = new Date().toISOString();
            const docId = yield (0, unifiedFirestoreService_1.unifiedAddDoc)('comments', {
                parentType: parent.type,
                parentId: parent.id,
                authorUid,
                authorName,
                authorAvatar: authorAvatar !== null && authorAvatar !== void 0 ? authorAvatar : null,
                text: text.trim(),
                createdAt: now,
                updatedAt: now,
                deleted: false,
                likeCount: 0,
                likedBy: [],
            });
            console.log(`✅ CommentsService: added comment ${docId} on ${parent.type}/${parent.id}`);
            return docId;
        });
    }
    static getTopComment(parent) {
        return __awaiter(this, void 0, void 0, function* () {
            // Uses the existing parentType+parentId+createdAt ASC index — no extra index needed.
            // Fetch a small window and pick the last non-deleted comment (most recent).
            const docs = yield (0, unifiedFirestoreService_1.unifiedCollection)('comments')
                .where('parentType', '==', parent.type)
                .where('parentId', '==', parent.id)
                .orderBy('createdAt', 'asc')
                .getDocs();
            const all = docs
                .map(d => (Object.assign({ id: d.id }, d.data())))
                .filter(c => !c.deleted);
            if (all.length === 0)
                return null;
            return all[all.length - 1];
        });
    }
    static listComments(parent) {
        return __awaiter(this, void 0, void 0, function* () {
            const docs = yield (0, unifiedFirestoreService_1.unifiedCollection)('comments')
                .where('parentType', '==', parent.type)
                .where('parentId', '==', parent.id)
                .orderBy('createdAt', 'asc')
                .getDocs();
            return docs
                .map(d => (Object.assign({ id: d.id }, d.data())))
                .filter(c => !c.deleted);
        });
    }
    /**
     * Subscribe to real-time comment updates.
     * Web: uses onSnapshot. Native (Capacitor): falls back to 15-second polling.
     * Returns an unsubscribe function.
     */
    static subscribeToComments(parent, callback) {
        if (!(0, firebaseManager_1.isFirebaseInitialized)()) {
            console.warn('CommentsService: Firebase not initialized — returning empty');
            callback([]);
            return () => { };
        }
        const isNative = core_1.Capacitor.isNativePlatform();
        if (isNative) {
            console.log('📱 CommentsService: native platform — using polling');
            let active = true;
            this.listComments(parent).then(callback);
            const intervalId = setInterval(() => __awaiter(this, void 0, void 0, function* () {
                if (active)
                    callback(yield this.listComments(parent));
            }), POLL_INTERVAL_MS);
            return () => {
                active = false;
                clearInterval(intervalId);
            };
        }
        else {
            console.log('🌐 CommentsService: web — using onSnapshot');
            const q = (0, firestore_1.query)((0, firestore_1.collection)((0, firebaseManager_1.getDb)(), 'comments'), (0, firestore_1.where)('parentType', '==', parent.type), (0, firestore_1.where)('parentId', '==', parent.id), (0, firestore_1.orderBy)('createdAt', 'asc'));
            return (0, firestore_1.onSnapshot)(q, snap => {
                const comments = snap.docs
                    .map(d => (Object.assign({ id: d.id }, d.data())))
                    .filter(c => !c.deleted);
                callback(comments);
            }, err => {
                console.error('CommentsService: onSnapshot error (index missing or permissions)', err);
                callback([]);
            });
        }
    }
    /** Soft-delete: set deleted=true. Cloud Function trigger decrements parent count. */
    static deleteComment(commentId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('comments', commentId, {
                deleted: true,
                updatedAt: new Date().toISOString(),
            });
            console.log(`🗑️ CommentsService: soft-deleted comment ${commentId}`);
        });
    }
    /**
     * Toggle the current user's like on a comment. Updates `likedBy` (source of truth)
     * and `likeCount` in lockstep. A read-modify-write is used instead of `arrayUnion`
     * / `arrayRemove` because the native Firestore plugin doesn't support FieldValue
     * sentinels — the same pattern used by review/watchlist likes elsewhere in the app.
     *
     * Returns the new liked state from the client's perspective. The CF
     * `onCommentLiked` trigger handles author notification.
     */
    static toggleCommentLike(commentId, uid) {
        var _a, _b, _c;
        return __awaiter(this, void 0, void 0, function* () {
            const snap = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('comments', commentId);
            if (!((_a = snap === null || snap === void 0 ? void 0 : snap.exists) === null || _a === void 0 ? void 0 : _a.call(snap))) {
                throw new Error(`comment ${commentId} not found`);
            }
            const data = (_c = (_b = snap.data) === null || _b === void 0 ? void 0 : _b.call(snap)) !== null && _c !== void 0 ? _c : snap;
            const currentLikedBy = Array.isArray(data === null || data === void 0 ? void 0 : data.likedBy) ? data.likedBy : [];
            const wasLiked = currentLikedBy.indexOf(uid) !== -1;
            const nextLikedBy = wasLiked
                ? currentLikedBy.filter(u => u !== uid)
                : [...currentLikedBy, uid];
            const nextCount = nextLikedBy.length;
            yield (0, unifiedFirestoreService_1.unifiedUpdateDoc)('comments', commentId, {
                likedBy: nextLikedBy,
                likeCount: nextCount,
                updatedAt: new Date().toISOString(),
            });
            console.log(`${wasLiked ? '💔' : '❤️'} CommentsService: ${wasLiked ? 'unliked' : 'liked'} ${commentId} by ${uid}`);
            return { isLiked: !wasLiked, likeCount: nextCount };
        });
    }
    /**
     * Write a moderation report for a comment.
     *
     * Reports land in a separate `commentReports/{commentId}_{reporterUid}` collection that
     * only admins can read — non-author / non-admin users cannot update the comment doc
     * itself per Firestore rules, so the previous in-place `reportedBy` update was failing
     * silently. The doc carries a denormalized snapshot (parent ref, author, text, time)
     * so the moderator console can render the report without extra fetches.
     *
     * Deterministic doc id (commentId_reporterUid) prevents duplicate reports from the
     * same user.
     */
    static reportComment(commentId, reporterUid) {
        var _a, _b, _c, _d, _e, _f, _g;
        return __awaiter(this, void 0, void 0, function* () {
            // Snapshot what's being reported so the moderator view doesn't need to refetch.
            const snap = yield (0, unifiedFirestoreService_1.unifiedGetDoc)('comments', commentId);
            if (!((_a = snap === null || snap === void 0 ? void 0 : snap.exists) === null || _a === void 0 ? void 0 : _a.call(snap)))
                return;
            const data = (_c = (_b = snap.data) === null || _b === void 0 ? void 0 : _b.call(snap)) !== null && _c !== void 0 ? _c : snap;
            const reportId = `${commentId}_${reporterUid}`;
            yield (0, unifiedFirestoreService_1.unifiedSetDoc)('commentReports', reportId, {
                commentId,
                reporterUid,
                parentType: (_d = data === null || data === void 0 ? void 0 : data.parentType) !== null && _d !== void 0 ? _d : null,
                parentId: (_e = data === null || data === void 0 ? void 0 : data.parentId) !== null && _e !== void 0 ? _e : null,
                commentAuthorUid: (_f = data === null || data === void 0 ? void 0 : data.authorUid) !== null && _f !== void 0 ? _f : null,
                commentAuthorName: (_g = data === null || data === void 0 ? void 0 : data.authorName) !== null && _g !== void 0 ? _g : null,
                commentText: typeof (data === null || data === void 0 ? void 0 : data.text) === 'string' ? data.text : '',
                reportedAt: new Date().toISOString(),
            }, /* merge */ false);
            console.log(`🚩 CommentsService: reported comment ${commentId} by ${reporterUid}`);
        });
    }
    /**
     * List comment reports for the moderator console. Returns the raw report docs ordered
     * most-recent-first. Caller is expected to group/dedupe by commentId for display.
     */
    static listReportedComments(limitCount = 100) {
        return __awaiter(this, void 0, void 0, function* () {
            const docs = yield (0, unifiedFirestoreService_1.unifiedCollection)('commentReports')
                .orderBy('reportedAt', 'desc')
                .limit(limitCount)
                .getDocs();
            return docs.map(d => {
                var _a, _b, _c, _d, _e, _f;
                const r = d.data();
                return {
                    id: d.id,
                    commentId: String((_a = r === null || r === void 0 ? void 0 : r.commentId) !== null && _a !== void 0 ? _a : ''),
                    reporterUid: String((_b = r === null || r === void 0 ? void 0 : r.reporterUid) !== null && _b !== void 0 ? _b : ''),
                    parentType: (_c = r === null || r === void 0 ? void 0 : r.parentType) !== null && _c !== void 0 ? _c : null,
                    parentId: (_d = r === null || r === void 0 ? void 0 : r.parentId) !== null && _d !== void 0 ? _d : null,
                    commentAuthorUid: (_e = r === null || r === void 0 ? void 0 : r.commentAuthorUid) !== null && _e !== void 0 ? _e : null,
                    commentAuthorName: (_f = r === null || r === void 0 ? void 0 : r.commentAuthorName) !== null && _f !== void 0 ? _f : null,
                    commentText: typeof (r === null || r === void 0 ? void 0 : r.commentText) === 'string' ? r.commentText : '',
                    reportedAt: typeof (r === null || r === void 0 ? void 0 : r.reportedAt) === 'string' ? r.reportedAt : '',
                };
            });
        });
    }
    /**
     * Dismiss all reports for a single comment — the moderator either acted on it (deleted
     * the comment / blocked the user) or judged it benign. Removes every report row whose
     * `commentId` matches.
     */
    static dismissReportsForComment(commentId) {
        return __awaiter(this, void 0, void 0, function* () {
            const docs = yield (0, unifiedFirestoreService_1.unifiedCollection)('commentReports')
                .where('commentId', '==', commentId)
                .getDocs();
            yield Promise.all(docs.map(d => (0, unifiedFirestoreService_1.unifiedDeleteDoc)('commentReports', d.id)));
            console.log(`✅ CommentsService: dismissed ${docs.length} report(s) for comment ${commentId}`);
            return docs.length;
        });
    }
}
exports.CommentsService = CommentsService;
